/* eslint-disable css-modules/no-unused-class */
import { FC, useEffect, useRef, useState } from 'react';
import { CardLoader, Tooltip, Icon, ProgressIndicator } from 'learning-panel-components';
import { translate } from 'translations';
import { PrimaryCourseProps, CourseLesson } from './primaryCourseTypes';
import PrimaryCourseEmptyStateBottom from './primaryCourseEmptyStateBottom';
import styles from './primaryCourse.module.scss';

const PrimaryCourse: FC<PrimaryCourseProps> = (props: PrimaryCourseProps) => {
    const {
        courseLessons,
        courseName,
        isLoading,
        courseUrl,
        isEmpty,
        onChangePrimaryCourse,
        score,
        iconUrl,
        completionPercentage,
        competedLessons,
        totalLessons,
        isLeanUser
    } = props;
    const [nextIndex, setNextIndex] = useState<number>();
    const [hoverIndex, setOverIndex] = useState<number>(-1);
    const currentLessonRef = useRef<null | HTMLAnchorElement>(null);
    const executeScroll = () => currentLessonRef?.current?.scrollIntoView();
    const useMountEffect = (fun: any) => useEffect(fun, [nextIndex]);
    useMountEffect(executeScroll);

    useEffect(() => {
        const indexesStatus = courseLessons.map(lesson => lesson.lessonStatus);
        const tempDoneIndexes: number[] = [];
        let tempNextIndex = -1;
        indexesStatus.forEach((lessonStatus, i) => {
            if ((lessonStatus === 'not_started' || lessonStatus === 'started') && tempNextIndex === -1)
                tempNextIndex = i;
            if (lessonStatus === 'completed') tempDoneIndexes.push(i);
        });
        setNextIndex(tempNextIndex);
    }, [courseLessons]);

    const getLessonElement = (lesson: CourseLesson, index: number) => {
        if (lesson.lessonStatus === 'completed')
            return (
                <div className={styles.completed}>
                    <Icon name="doneBlue" className={styles.icon} dataTestid="done-icon" />
                    <div className={styles.text}>{lesson.lessonDisplayName}</div>
                    {hoverIndex === index && (
                        <div className={styles['icon-wrapper']}>
                            <Tooltip title={translate('retake')} placement="top-start" enterDelay open key={index}>
                                <span>
                                    <Icon name="reTakeAction" className={styles['retake-action']} />
                                </span>
                            </Tooltip>
                        </div>
                    )}
                </div>
            );
        if (index === nextIndex)
            return (
                <div className={styles.next}>
                    <Icon name="playNextLesson" className={styles.icon} dataTestid="dot-icon" />
                    <div className={styles.text}>{lesson.lessonDisplayName}</div>
                </div>
            );
        return (
            <div className={styles['not-done']}>
                <div className={styles.text}>{lesson.lessonDisplayName}</div>
            </div>
        );
    };

    return isLoading ? (
        <CardLoader width="100%" height="50%" />
    ) : (
        <div data-testid="primary-course" className={styles['primary-course']}>
            <div className={styles.top}>
                <div className={styles.titles}>
                    {!isEmpty ? (
                        <>
                            <div className={styles['title-wrapper']}>
                                <div className={styles.title}>{translate('PrimaryCourse')}</div>
                                <div className={styles['icon-wrapper']} onClick={() => onChangePrimaryCourse()}>
                                    <Tooltip title={translate('ChangePrimaryCourse')} placement="right">
                                        <span>
                                            <Icon name="settings" className={styles['change-primary-course']} />
                                        </span>
                                    </Tooltip>
                                </div>
                            </div>
                            <a href={courseUrl}>
                                <div className={styles.name}>{courseName}</div>
                            </a>
                        </>
                    ) : (
                        <div className={styles['empty-name']}>{translate('WaiForIt')}</div>
                    )}
                </div>
                <div className={styles['course-progress-wrapper']}>
                    <img src={iconUrl} className={styles.image} alt="primaryCourseIcon" />
                    <div className={styles['first-detail']}>
                        <div className={styles['top-detail']}>
                            <div className={styles.title}>{translate('Progress')}</div>
                            <div className={styles.value}>{Math.trunc(completionPercentage)}%</div>
                        </div>
                        <ProgressIndicator direction={'horizontal'} value={completionPercentage} height={'100%'} />
                    </div>
                    <hr />
                    <div className={styles['top-detail']}>
                        <div className={styles.title}>{translate('LessonsCompleted')}</div>
                        <div className={styles.value}>{`${competedLessons}/${totalLessons}`}</div>
                    </div>
                    <hr />
                    <div className={styles['top-detail']}>
                        <div className={styles.title}>{translate('DevCoins')}</div>
                        <div className={styles.value}>
                            <Icon name="devCoin" />
                            <div>{score?.toLocaleString()}</div>
                        </div>
                    </div>
                </div>
            </div>
            {!isEmpty ? (
                <div className={styles['lessons-wrapper']}>
                    <hr />
                    <div className={styles['lessons-title']}>{translate('LessonsOnThisCourse')}</div>
                    <hr />
                    <div className={styles.lessons}>
                        {courseLessons.map((lesson, i) =>
                            isLeanUser && !lesson.activeOnLean ? (
                                <div className={styles['not-allowed']}>
                                    <div className={styles.text}>{lesson.lessonDisplayName}</div>
                                </div>
                            ) : (
                                <a
                                    ref={i === nextIndex ? currentLessonRef : undefined}
                                    key={i}
                                    href={lesson.lessonUrl}
                                    onMouseEnter={() => {
                                        setOverIndex(i);
                                    }}
                                    onMouseLeave={() => {
                                        setOverIndex(-1);
                                    }}
                                >
                                    {getLessonElement(lesson, i)}
                                </a>
                            )
                        )}
                    </div>
                </div>
            ) : (
                <div className={styles['empty-wrapper']}>
                    <div className={styles['empty-text']}>{translate('noPrimaryCourseMessage')}</div>
                    <PrimaryCourseEmptyStateBottom />
                </div>
            )}
            {isEmpty ? <div className={styles['bottom-empty']}></div> : <div></div>}
        </div>
    );
};

export default PrimaryCourse;
